import React, { useState, useEffect, useCallback, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import { Col, Row, Modal, ModalBody, Card, Alert } from "reactstrap";
import { Flex, Empty, Badge, Checkbox, Collapse, Tag } from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import moment from "moment";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import type {
  AppDetail,
  AppMappedModule,
  ModuleTreeGroup,
} from "../../../types/super-admin";

const AppDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const formMethods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, reset, formState: { dirtyFields } } = formMethods;

  const [app, setApp] = useState<AppDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [mapModal, setMapModal] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  // Module mapping state
  const [moduleTree, setModuleTree] = useState<ModuleTreeGroup[]>([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([]);

  const fetchAppDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await superAdminService.getApplicationById(id);
      if (res.status && res.data) {
        setApp(res.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch application:", error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchAppDetails();
  }, [fetchAppDetails]);

  const openEditModal = () => {
    if (!app) return;
    setAlertInfo({ type: "", message: "", errors: [] });
    setValue("appCode", app.appCode || "");
    setValue("appName", app.appName || "");
    setValue("description", app.description || "");
    setEditModal(true);
  };

  const submitEdit = async (data: any) => {
    if (!app) return;
    setEditLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = { appId: app.appId };

      if (dirtyFields.appCode) payload.appCode = typeof data.appCode === "string" ? data.appCode.trim() : data.appCode;
      if (dirtyFields.appName) payload.appName = typeof data.appName === "string" ? data.appName.trim() : data.appName;
      if (dirtyFields.description) payload.description = typeof data.description === "string" ? data.description.trim() : data.description;

      if (Object.keys(payload).length === 1) {
        setAlertInfo({ type: "info", message: "No changes detected.", errors: [] });
        setEditLoading(false);
        return;
      }

      const res = await superAdminService.updateApplication(payload);
      if (res.status) {
        Swal.fire("Updated!", "Application has been updated.", "success");
        setEditModal(false);
        reset();
        fetchAppDetails();
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setEditLoading(false);
    }
  };

  const fetchModuleOptions = async () => {
    try {
      const res = await superAdminService.getModuleDropdown();
      if (res.status && res.data) {
        setModuleTree(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error: any) {
      console.error("Failed to fetch modules:", error.message);
    }
  };

  const openMapModal = async () => {
    setAlertInfo({ type: "", message: "", errors: [] });
    await fetchModuleOptions();
    // Pre-select already-mapped modules
    setSelectedModuleIds(app?.modules?.map((m) => m.moduleId) || []);
    setMapModal(true);
  };

  const handleSaveModuleChanges = async () => {
    if (!app) return;
    setMapLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const currentMappedIds = new Set(app.modules?.map((m) => m.moduleId) || []);
      const newSelectedIds = new Set(selectedModuleIds);

      // Modules to add (selected but not currently mapped)
      const toAdd = selectedModuleIds.filter((id) => !currentMappedIds.has(id));
      // Modules to remove (currently mapped but unchecked)
      const toRemove = Array.from(currentMappedIds).filter((id) => !newSelectedIds.has(id));

      let hasError = false;

      // Map new modules
      if (toAdd.length > 0) {
        const res = await superAdminService.mapModulesToApp({
          appId: app.appId,
          moduleIds: toAdd,
        });
        if (!res.status) {
          hasError = true;
          const errorMsg = Array.isArray(res.message)
            ? res.message.join(", ")
            : res.message || "Failed to map modules";
          setAlertInfo({ type: "error", message: errorMsg, errors: [] });
        }
      }

      // Unmap removed modules (bulk)
      if (toRemove.length > 0) {
        try {
          const res = await superAdminService.bulkUnmapModulesFromApp({
            appId: app.appId,
            moduleIds: toRemove,
          });
          if (!res.status) {
            hasError = true;
          }
        } catch {
          hasError = true;
        }
      }

      if (!hasError) {
        Swal.fire("Saved!", "Module mappings have been updated successfully.", "success");
        setMapModal(false);
        setSelectedModuleIds([]);
        fetchAppDetails();
      } else if (toAdd.length > 0 || toRemove.length > 0) {
        // Partial success — refresh to show current state
        fetchAppDetails();
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setMapLoading(false);
    }
  };

  const handleUnmapModule = async (moduleId: number) => {
    if (!app) return;
    Swal.fire({
      title: "Unmap Module?",
      text: "This will remove the module from this application.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, unmap!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.unmapModuleFromApp({
            appId: app.appId,
            moduleId,
          });
          if (res.status) {
            Swal.fire("Unmapped!", "Module has been removed.", "success");
            fetchAppDetails();
          } else {
            Swal.fire("Error", res.message || "Failed to unmap", "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  // Set of already-mapped module IDs
  const mappedModuleIds = new Set(app?.modules?.map((m) => m.moduleId) || []);


  if (loading) {
    return (
      <React.Fragment>
        <Head title="Application Details" />
        <Content>
          <LoadingSpinner height="300px" />
        </Content>
      </React.Fragment>
    );
  }

  if (!app) {
    return (
      <React.Fragment>
        <Head title="Application Details" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Application not found" />
            <Button
              color="primary"
              className="mt-3"
              onClick={() => navigate("/sa-applications")}
            >
              Back to List
            </Button>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title="Application Details" />
      <Content>
        {/* Header */}
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle page>
                {app.appCode ? `${app.appCode} — ` : ""}
                {app.appName}{" "}
                <Badge color={app.status === "Active" ? "green" : "red"}>
                  {app.status}
                </Badge>
              </BlockTitle>
              <BlockDes className="text-soft">
                <ul className="list-inline">
                  <li>
                    Modules:{" "}
                    <span className="text-base">{app.modules?.length || 0}</span>
                  </li>
                </ul>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <Link to="/sa-applications">
                    <Button outline className="bg-white d-none d-sm-inline-flex">
                      <Icon name="arrow-left"></Icon>
                      <span>Back</span>
                    </Button>
                  </Link>
                </li>
                <li>
                  <Button color="primary" onClick={openEditModal}>
                    <Icon name="edit-alt-fill"></Icon>
                    <span>Edit</span>
                  </Button>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {/* App Info */}
        <Block>
          <Row className="gy-5">
            <Col lg="6">
              <BlockHead>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h5">Application Information</BlockTitle>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Card className="card-bordered">
                <ul className="data-list is-compact">
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">App Code</div>
                      <div className="data-value">{app.appCode || "-"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Application Name</div>
                      <div className="data-value">{app.appName}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Description</div>
                      <div className="data-value">{app.description || "-"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Status</div>
                      <div className="data-value">
                        <span
                          className={`tb-status text-${
                            app.status === "Active" ? "success" : "danger"
                          }`}
                        >
                          {app.status}
                        </span>
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Created</div>
                      <div className="data-value">
                        {moment.utc(app.createdAt).local().format("DD MMM YYYY hh:mm A")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Updated</div>
                      <div className="data-value">
                        {moment.utc(app.updatedAt).local().format("DD MMM YYYY hh:mm A")}
                      </div>
                    </div>
                  </li>
                </ul>
              </Card>
            </Col>
          </Row>
        </Block>

        {/* Mapped Modules */}
        <Block>
          <BlockHead size="sm">
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle tag="h5">
                  Mapped Modules ({app.modules?.length || 0})
                </BlockTitle>
              </BlockHeadContent>
              <BlockHeadContent>
                <Button color="primary" size="sm" onClick={openMapModal}>
                  <Icon name="plus-sm"></Icon>
                  <span>Map Modules</span>
                </Button>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>
          <Card className="card-bordered">
            <div className="card-inner">
              {app.modules && app.modules.length > 0 ? (
                <Flex wrap="wrap" gap="small">
                  {app.modules.map((mod) => (
                    <Tag
                      key={mod.mappingId}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        handleUnmapModule(mod.moduleId);
                      }}
                      color="geekblue"
                      style={{
                        fontSize: "13.5px",
                        fontWeight: 500,
                        padding: "6px 14px",
                        borderRadius: "6px",
                        lineHeight: "1.4",
                      }}
                    >
                      {mod.moduleName}
                    </Tag>
                  ))}
                </Flex>
              ) : (
                <Empty description="No modules mapped to this application. Click 'Map Modules' to add." />
              )}
            </div>
          </Card>
        </Block>
      </Content>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(e) => {
              e.preventDefault();
              setEditModal(false);
              reset();
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="p-2">
            {alertInfo.message && editModal && (
              <div className="mb-3 mt-1">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}

            <h5 className="title">Edit Application</h5>
            <div className="mt-4">
              <FormProvider {...formMethods}>
                <form
                  className="content clearfix"
                  onSubmit={handleSubmit(submitEdit)}
                >
                  <Row className="gy-4">
                    <Col lg="12">
                      <FormInput
                        name="appCode"
                        label="App Code"
                        type="text"
                        maxLength={50}
                        placeholder="Enter app code"
                      />
                    </Col>
                    <Col lg="12">
                      <FormInput
                        name="appName"
                        label="Application Name"
                        type="text"
                        maxLength={200}
                        placeholder="Enter application name"
                      />
                    </Col>
                    <Col lg="12">
                      <FormInput
                        name="description"
                        label="Description"
                        type="text"
                        maxLength={500}
                        placeholder="Enter description"
                      />
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col lg="12">
                      <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                        <li>
                          <Button color="primary" size="md" type="submit">
                            {editLoading ? (
                              <LoadingSpinner size="sm" inline />
                            ) : (
                              "Save Changes"
                            )}
                          </Button>
                        </li>
                        <li>
                          <a
                            href="#cancel"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!editLoading) {
                                setEditModal(false);
                                reset();
                              }
                            }}
                            className={`link link-light${editLoading ? " disabled text-muted" : ""}`}
                            style={editLoading ? { pointerEvents: "none" } : {}}
                          >
                            Cancel
                          </a>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </form>
              </FormProvider>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Map Modules Modal */}
      <Modal
        isOpen={mapModal}
        toggle={() => setMapModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(e) => {
              e.preventDefault();
              setMapModal(false);
              setSelectedModuleIds([]);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="p-2">
            {alertInfo.message && mapModal && (
              <div className="mb-3 mt-1">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}

            <h5 className="title">Manage Modules — {app?.appName}</h5>
            <p className="text-soft">
              Check or uncheck modules to update mappings for this application.
            </p>
            <div className="mt-4">
              <div className="form-group">
                <label className="form-label">Modules</label>
                <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e5e9f2", borderRadius: 6, padding: 12 }}>
                  {moduleTree.length > 0 ? (
                    <Collapse
                      defaultActiveKey={moduleTree.map((g) => g.category)}
                      ghost
                      size="small"
                    >
                      {moduleTree.map((group) => (
                        <Collapse.Panel
                          key={group.category}
                          header={<span style={{ fontWeight: 600, fontSize: 14 }}>{group.category}</span>}
                        >
                          {group.modules.map((parent) => (
                            <div key={parent.moduleId} style={{ marginBottom: 12 }}>
                              {parent.isParent && parent.children.length > 0 ? (
                                <>
                                  <div style={{ fontWeight: 500, color: "#555", marginBottom: 6, fontSize: 13 }}>
                                    {parent.moduleName}
                                  </div>
                                  <div style={{ paddingLeft: 20, display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
                                    {parent.children.map((child) => (
                                      <Checkbox
                                        key={child.moduleId}
                                        checked={selectedModuleIds.includes(child.moduleId)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedModuleIds((prev) => [...prev, child.moduleId]);
                                          } else {
                                            setSelectedModuleIds((prev) => prev.filter((id) => id !== child.moduleId));
                                          }
                                        }}
                                      >
                                        {child.moduleName}
                                      </Checkbox>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <Checkbox
                                  checked={selectedModuleIds.includes(parent.moduleId)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedModuleIds((prev) => [...prev, parent.moduleId]);
                                    } else {
                                      setSelectedModuleIds((prev) => prev.filter((id) => id !== parent.moduleId));
                                    }
                                  }}
                                >
                                  {parent.moduleName}
                                </Checkbox>
                              )}
                            </div>
                          ))}
                        </Collapse.Panel>
                      ))}
                    </Collapse>
                  ) : (
                    <Empty description="No modules available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </div>
                <div className="mt-2">
                  <span style={{ fontSize: 12, color: "#8094ae" }}>
                    {selectedModuleIds.length} module{selectedModuleIds.length !== 1 ? "s" : ""} selected
                  </span>
                </div>
              </div>

              <Row className="mt-4">
                <Col lg="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="md"
                        onClick={handleSaveModuleChanges}
                      >
                        {mapLoading ? (
                          <LoadingSpinner size="sm" inline />
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                    </li>
                    <li>
                      <a
                        href="#cancel"
                        onClick={(e) => {
                          e.preventDefault();
                          if (!mapLoading) {
                            setMapModal(false);
                            setSelectedModuleIds([]);
                          }
                        }}
                        className={`link link-light${mapLoading ? " disabled text-muted" : ""}`}
                        style={mapLoading ? { pointerEvents: "none" } : {}}
                      >
                        Cancel
                      </a>
                    </li>
                  </ul>
                </Col>
              </Row>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AppDetails;
